import React, { useEffect, useMemo, useState } from 'react';
import { fetchScreenerStocks, ScreenerStock } from '../services/screenerApi';

type Props = {
  isDarkMode: boolean;
  authToken?: string;
  onUnauthorized?: () => void;
};

type SortKey = 'score' | 'marketCap' | 'price' | 'garpMatch';

type GarpThresholds = {
  minRevenueGrowthPct: number;
  minEpsGrowthPct: number;
  minOperatingIncomeGrowthPct: number;
  maxPeg: number;
  minRoicPct: number;
  minOperatingMarginTrendPct: number;
  maxDebtToEquity: number;
};

type MetricAvailability = {
  revenueGrowth: boolean;
  epsGrowth: boolean;
  operatingIncomeGrowth: boolean;
  peg: boolean;
  roic: boolean;
  operatingMarginTrend: boolean;
  debtToEquity: boolean;
};

type CriterionResult = {
  key: string;
  label: string;
  value: number | null;
  passed: boolean;
  considered: boolean;
};

const DEFAULT_THRESHOLDS: GarpThresholds = {
  minRevenueGrowthPct: 8,
  minEpsGrowthPct: 10,
  minOperatingIncomeGrowthPct: 0,
  maxPeg: 2,
  minRoicPct: 10,
  minOperatingMarginTrendPct: 0,
  maxDebtToEquity: 1.2,
};

const parseNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const firstNumber = (stock: ScreenerStock, keys: string[]): number | null => {
  const dynamic = stock as unknown as Record<string, unknown>;
  for (const key of keys) {
    const n = parseNumber(dynamic[key]);
    if (n != null) return n;
  }
  return null;
};

const buildCriteria = (
  stock: ScreenerStock,
  thresholds: GarpThresholds,
  ignoreMissingMetrics: boolean,
): CriterionResult[] => {
  const revenueGrowth = firstNumber(stock, ['revenueGrowthPct', 'revenueGrowth']);
  const epsGrowth = firstNumber(stock, ['epsGrowthPct', 'epsGrowth']);
  const operatingIncomeGrowth = firstNumber(stock, ['operatingIncomeGrowthPct', 'operatingIncomeGrowth']);
  const peg = firstNumber(stock, ['peg', 'pegRatio']);
  const roic = firstNumber(stock, ['roicPct', 'roic']);
  const operatingMarginTrend = firstNumber(stock, ['operatingMarginTrendPct', 'operatingMarginDeltaPct']);
  const debtToEquity = firstNumber(stock, ['debtToEquity', 'debtToEbitda']);

  const evaluate = (
    key: string,
    label: string,
    value: number | null,
    predicate: (v: number) => boolean,
  ): CriterionResult => {
    if (value == null) {
      return {
        key,
        label,
        value,
        passed: ignoreMissingMetrics,
        considered: !ignoreMissingMetrics,
      };
    }

    return {
      key,
      label,
      value,
      passed: predicate(value),
      considered: true,
    };
  };

  return [
    evaluate('revGrowth', `Revenue Growth >= ${thresholds.minRevenueGrowthPct}%`, revenueGrowth, (v) => v >= thresholds.minRevenueGrowthPct),
    evaluate('epsGrowth', `EPS Growth >= ${thresholds.minEpsGrowthPct}%`, epsGrowth, (v) => v >= thresholds.minEpsGrowthPct),
    evaluate('opIncomeGrowth', `Operating Income Growth >= ${thresholds.minOperatingIncomeGrowthPct}%`, operatingIncomeGrowth, (v) => v >= thresholds.minOperatingIncomeGrowthPct),
    evaluate('peg', `PEG <= ${thresholds.maxPeg}`, peg, (v) => v <= thresholds.maxPeg),
    evaluate('roic', `ROIC >= ${thresholds.minRoicPct}%`, roic, (v) => v >= thresholds.minRoicPct),
    evaluate('opMarginTrend', `Operating Margin Trend >= ${thresholds.minOperatingMarginTrendPct}%`, operatingMarginTrend, (v) => v >= thresholds.minOperatingMarginTrendPct),
    evaluate('debt', `Debt To Equity <= ${thresholds.maxDebtToEquity}`, debtToEquity, (v) => v <= thresholds.maxDebtToEquity),
  ];
};

const ScreenerScreen: React.FC<Props> = ({ isDarkMode, authToken, onUnauthorized }) => {
  const [stocks, setStocks] = useState<ScreenerStock[]>([]);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [sector, setSector] = useState('All');
  const [exchange, setExchange] = useState('All');
  const [mode, setMode] = useState<'GARP' | 'Value'>('GARP');
  const [minimumScore, setMinimumScore] = useState(50);
  const [sort, setSort] = useState<SortKey>('garpMatch');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [enableGarpCriteria, setEnableGarpCriteria] = useState(false);
  const [ignoreMissingMetrics, setIgnoreMissingMetrics] = useState(false);
  const [minimumPassedCriteria, setMinimumPassedCriteria] = useState(4);
  const [thresholds, setThresholds] = useState<GarpThresholds>(DEFAULT_THRESHOLDS);

  useEffect(() => {
    if (!authToken) return;

    fetchScreenerStocks(authToken)
      .then((result) => {
        setStocks(result.stocks);
        setUpdatedAt(result.lastUpdatedUtc);
        setLoading(false);
      })
      .catch((reason: any) => {
        setLoading(false);
        if (reason?.message?.includes('(401)')) onUnauthorized?.();
        setError(reason?.message || 'Unable to load screener data');
      });
  }, [authToken, onUnauthorized]);

  const sectors = useMemo(
    () => ['All', ...Array.from(new Set(stocks.map((x) => x.sector).filter(Boolean))).sort()],
    [stocks],
  );

  const exchanges = useMemo(
    () => ['All', ...Array.from(new Set(stocks.map((x) => x.exchangeShortName).filter(Boolean))).sort()],
    [stocks],
  );

  const scoreFor = (stock: ScreenerStock) =>
    mode === 'GARP' ? stock.garPProxyScore : stock.valueProxyScore;

  const metricAvailability = useMemo<MetricAvailability>(() => ({
    revenueGrowth: stocks.some((stock) => firstNumber(stock, ['revenueGrowthPct', 'revenueGrowth']) != null),
    epsGrowth: stocks.some((stock) => firstNumber(stock, ['epsGrowthPct', 'epsGrowth']) != null),
    operatingIncomeGrowth: stocks.some((stock) => firstNumber(stock, ['operatingIncomeGrowthPct', 'operatingIncomeGrowth']) != null),
    peg: stocks.some((stock) => firstNumber(stock, ['peg', 'pegRatio']) != null),
    roic: stocks.some((stock) => firstNumber(stock, ['roicPct', 'roic']) != null),
    operatingMarginTrend: stocks.some((stock) => firstNumber(stock, ['operatingMarginTrendPct', 'operatingMarginDeltaPct']) != null),
    debtToEquity: stocks.some((stock) => firstNumber(stock, ['debtToEquity', 'debtToEbitda']) != null),
  }), [stocks]);

  const advancedMetricsAvailable = useMemo(
    () => Object.values(metricAvailability).some(Boolean),
    [metricAvailability],
  );

  const roicRowsCount = useMemo(
    () => stocks.filter((stock) => firstNumber(stock, ['roicPct', 'roic']) != null).length,
    [stocks],
  );

  const availableCriteriaCount = useMemo(() => {
    return Object.values(metricAvailability).filter(Boolean).length;
  }, [metricAvailability]);

  const effectiveMinimumPassedCriteria = useMemo(() => {
    if (!enableGarpCriteria) return minimumPassedCriteria;
    if (minimumPassedCriteria <= 0) return 0;
    // Keep filtering usable when only part of the metric set is currently enriched.
    return Math.min(minimumPassedCriteria, Math.max(1, availableCriteriaCount));
  }, [availableCriteriaCount, enableGarpCriteria, minimumPassedCriteria]);

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();

    const base = stocks
      .filter(
        (stock) =>
          (!needle || `${stock.symbol} ${stock.companyName}`.toLowerCase().includes(needle)) &&
          (sector === 'All' || stock.sector === sector) &&
          (exchange === 'All' || stock.exchangeShortName === exchange) &&
          scoreFor(stock) >= minimumScore,
      )
      .map((stock) => {
        const criteria = buildCriteria(stock, thresholds, ignoreMissingMetrics || !advancedMetricsAvailable);
        const passed = criteria.filter((x) => x.passed).length;
        const considered = criteria.filter((x) => x.considered).length;
        return { stock, criteria, passed, considered };
      });

    const filtered = enableGarpCriteria
      ? base.filter((row) => row.passed >= effectiveMinimumPassedCriteria)
      : base;

    return filtered.sort((a, b) => {
      if (sort === 'marketCap') return (b.stock.marketCap ?? 0) - (a.stock.marketCap ?? 0);
      if (sort === 'price') return (b.stock.price ?? 0) - (a.stock.price ?? 0);
      if (sort === 'garpMatch') {
        if (b.passed !== a.passed) return b.passed - a.passed;
        return scoreFor(b.stock) - scoreFor(a.stock);
      }
      return scoreFor(b.stock) - scoreFor(a.stock);
    });
  }, [
    enableGarpCriteria,
    advancedMetricsAvailable,
    exchange,
    ignoreMissingMetrics,
    minimumPassedCriteria,
    effectiveMinimumPassedCriteria,
    minimumScore,
    mode,
    query,
    sector,
    sort,
    stocks,
    thresholds,
  ]);

  const applyRecommendedGarp = () => {
    setEnableGarpCriteria(true);
    setIgnoreMissingMetrics(true);
    setMinimumPassedCriteria(4);
    setThresholds(DEFAULT_THRESHOLDS);
  };

  const updateThreshold = <K extends keyof GarpThresholds>(key: K, value: number) => {
    setEnableGarpCriteria(true);
    setIgnoreMissingMetrics(false);
    setMinimumPassedCriteria((previous) => {
      const requested = Math.max(1, previous);
      return Math.min(requested, Math.max(1, availableCriteriaCount));
    });
    setThresholds((previous) => ({ ...previous, [key]: value }));
  };

  const dark = isDarkMode;
  const money = (value: number | null) =>
    value == null ? '-' : value >= 1e9 ? `$${(value / 1e9).toFixed(1)}B` : `$${(value / 1e6).toFixed(0)}M`;

  const criterionLabelStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 6,
    fontSize: 13,
    lineHeight: 1.25,
    color: dark ? '#cdd7dd' : '#41505a',
  };

  const unavailableMetricHint = (available: boolean) => (
    <span
      style={{
        fontSize: 11,
        color: dark ? '#fbbf24' : '#a16207',
        minHeight: 14,
        visibility: available ? 'hidden' : 'visible',
      }}
    >
      Not available in snapshot
    </span>
  );

  const panelStyle: React.CSSProperties = {
    marginTop: 24,
    padding: 18,
    background: dark ? '#17232d' : '#ffffff',
    border: `1px solid ${dark ? '#2c3b46' : '#dce5e8'}`,
    borderRadius: 10,
  };

  return (
    <main
      style={{
        minHeight: 'calc(100vh - 4.375rem)',
        padding: '32px clamp(18px, 4vw, 64px)',
        background: dark ? '#101820' : '#f4f7f8',
        color: dark ? '#f3f4f6' : '#17232b',
      }}
    >
      <div style={{ maxWidth: 1500, margin: '0 auto' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 16,
            flexWrap: 'wrap',
            alignItems: 'end',
          }}
        >
          <div>
            <div style={{ color: '#0f766e', fontWeight: 800, letterSpacing: 1.5, fontSize: 12 }}>
              MARKET DISCOVERY
            </div>
            <h1 style={{ margin: '8px 0', fontSize: 36 }}>Screener</h1>
            <p style={{ margin: 0, color: dark ? '#9ca3af' : '#60717b' }}>
              סינון מניות לפי GARP/Value, כולל כפתור קריטריונים מומלצים ומדרגות הקשחה.
            </p>
          </div>
          <div style={{ fontSize: 13, color: dark ? '#9ca3af' : '#60717b' }}>
            {updatedAt
              ? `Data refreshed ${new Date(updatedAt).toLocaleString()}`
              : 'No daily snapshot yet'}
          </div>
        </div>

        <section style={panelStyle}>
          <div style={panelHeadingStyle(dark)}>
            <span>Universe</span>
            <span style={panelHintStyle(dark)}>Search and rank the cached market snapshot</span>
          </div>
          <div style={controlGridStyle}>
            <label style={criterionLabelStyle}>
              Search
              <input aria-label="Search" placeholder="Symbol or company" value={query} onChange={(e) => setQuery(e.target.value)} style={inputStyle(dark)} />
            </label>
            <label style={criterionLabelStyle}>
              Model
              <select value={mode} onChange={(e) => setMode(e.target.value as 'GARP' | 'Value')} style={inputStyle(dark)}><option>GARP</option><option>Value</option></select>
            </label>
            <label style={criterionLabelStyle}>
              Sector
              <select value={sector} onChange={(e) => setSector(e.target.value)} style={inputStyle(dark)}>{sectors.map((x) => <option key={x}>{x}</option>)}</select>
            </label>
            <label style={criterionLabelStyle}>
              Exchange
              <select value={exchange} onChange={(e) => setExchange(e.target.value)} style={inputStyle(dark)}>{exchanges.map((x) => <option key={x}>{x}</option>)}</select>
            </label>
            <label style={criterionLabelStyle}>
              Minimum proxy score <strong style={{ color: dark ? '#f3f4f6' : '#17232b' }}>{minimumScore}</strong>
              <input aria-label="Minimum proxy score" type="range" min="0" max="100" value={minimumScore} onChange={(e) => setMinimumScore(Number(e.target.value))} style={{ width: '100%' }} />
            </label>
            <label style={criterionLabelStyle}>
              Sort by
              <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)} style={inputStyle(dark)}><option value="garpMatch">GARP match</option><option value="score">Score</option><option value="marketCap">Market cap</option><option value="price">Price</option></select>
            </label>
          </div>
          <div style={actionRowStyle}>
            <button type="button" onClick={applyRecommendedGarp} style={ctaButtonStyle}>Apply Recommended GARP</button>
            <button type="button" onClick={() => setEnableGarpCriteria((prev) => !prev)} style={{ ...ghostButtonStyle, borderColor: enableGarpCriteria ? '#0f766e' : '#8ea0aa' }}>{enableGarpCriteria ? 'GARP filter on' : 'GARP filter off'}</button>
          </div>
        </section>

        <section
          style={{
            ...panelStyle,
            marginTop: 12,
            background: dark ? '#14222b' : '#fcfefe',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 16,
          }}
        >
          <label style={criterionLabelStyle}>
            Revenue Growth %
            {unavailableMetricHint(metricAvailability.revenueGrowth)}
            <input
              type="number"
              value={thresholds.minRevenueGrowthPct}
              onChange={(e) => updateThreshold('minRevenueGrowthPct', Number(e.target.value))}
              disabled={!metricAvailability.revenueGrowth}
              style={inputStyle(dark)}
            />
          </label>

          <label style={criterionLabelStyle}>
            EPS Growth %
            {unavailableMetricHint(metricAvailability.epsGrowth)}
            <input
              type="number"
              value={thresholds.minEpsGrowthPct}
              onChange={(e) => updateThreshold('minEpsGrowthPct', Number(e.target.value))}
              disabled={!metricAvailability.epsGrowth}
              style={inputStyle(dark)}
            />
          </label>

          <label style={criterionLabelStyle}>
            Operating Income Growth %
            {unavailableMetricHint(metricAvailability.operatingIncomeGrowth)}
            <input
              type="number"
              value={thresholds.minOperatingIncomeGrowthPct}
              onChange={(e) => updateThreshold('minOperatingIncomeGrowthPct', Number(e.target.value))}
              disabled={!metricAvailability.operatingIncomeGrowth}
              style={inputStyle(dark)}
            />
          </label>

          <label style={criterionLabelStyle}>
            Max PEG
            {unavailableMetricHint(metricAvailability.peg)}
            <input
              type="number"
              step="0.1"
              value={thresholds.maxPeg}
              onChange={(e) => updateThreshold('maxPeg', Number(e.target.value))}
              disabled={!metricAvailability.peg}
              style={inputStyle(dark)}
            />
          </label>

          <label style={criterionLabelStyle}>
            Min ROIC %
            {unavailableMetricHint(metricAvailability.roic)}
            <input
              type="number"
              value={thresholds.minRoicPct}
              onChange={(e) => updateThreshold('minRoicPct', Number(e.target.value))}
              disabled={!metricAvailability.roic}
              style={inputStyle(dark)}
            />
          </label>

          <label style={criterionLabelStyle}>
            Min Op Margin Trend %
            {unavailableMetricHint(metricAvailability.operatingMarginTrend)}
            <input
              type="number"
              value={thresholds.minOperatingMarginTrendPct}
              onChange={(e) => updateThreshold('minOperatingMarginTrendPct', Number(e.target.value))}
              disabled={!metricAvailability.operatingMarginTrend}
              style={inputStyle(dark)}
            />
          </label>

          <label style={criterionLabelStyle}>
            Max Debt To Equity
            {unavailableMetricHint(metricAvailability.debtToEquity)}
            <input
              type="number"
              step="0.1"
              value={thresholds.maxDebtToEquity}
              onChange={(e) => updateThreshold('maxDebtToEquity', Number(e.target.value))}
              disabled={!metricAvailability.debtToEquity}
              style={inputStyle(dark)}
            />
          </label>

          <label style={criterionLabelStyle}>
            Min Passed Criteria
            <input
              type="number"
              min={0}
              max={7}
              value={minimumPassedCriteria}
              onChange={(e) => setMinimumPassedCriteria(Number(e.target.value))}
              style={inputStyle(dark)}
            />
          </label>

          <label style={{ ...criterionLabelStyle, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={ignoreMissingMetrics}
              onChange={(e) => setIgnoreMissingMetrics(e.target.checked)}
            />
            Ignore missing metrics
          </label>
        </section>

        <div
          style={{
            display: 'flex',
            gap: 18,
            margin: '18px 0',
            color: dark ? '#aebbc2' : '#60717b',
            fontSize: 14,
            flexWrap: 'wrap',
          }}
        >
          <span>{rows.length.toLocaleString()} matches</span>
          <span>{stocks.length.toLocaleString()} cached stocks</span>
          <span>ROIC values: {roicRowsCount.toLocaleString()} / {stocks.length.toLocaleString()}</span>
          <span>One FMP snapshot per UTC day</span>
          <span>
            {enableGarpCriteria
              ? `GARP criteria ON (>=${effectiveMinimumPassedCriteria} pass)`
              : 'GARP criteria OFF'}
          </span>
          {enableGarpCriteria && minimumPassedCriteria !== effectiveMinimumPassedCriteria && (
            <span style={{ color: dark ? '#fbbf24' : '#a16207' }}>
              Requested minimum passed criteria ({minimumPassedCriteria}) was adjusted to {effectiveMinimumPassedCriteria} based on available metrics.
            </span>
          )}
          {enableGarpCriteria && minimumPassedCriteria === 0 && (
            <span style={{ color: dark ? '#fbbf24' : '#a16207' }}>
              Minimum passed criteria is 0, so threshold filters are disabled.
            </span>
          )}
          <span style={{ color: dark ? '#fbbf24' : '#a16207' }}>
            {advancedMetricsAvailable
              ? 'ROIC data loaded from the daily financial snapshot.'
              : 'ROIC data is not available in the current screener snapshot.'}
          </span>
          {enableGarpCriteria && minimumPassedCriteria > availableCriteriaCount && (
            <span style={{ color: dark ? '#fbbf24' : '#a16207' }}>
              Only {availableCriteriaCount} metric{availableCriteriaCount === 1 ? '' : 's'} available, but {minimumPassedCriteria} passed criteria required.
            </span>
          )}
        </div>

        {loading && <p>Loading cached screener...</p>}
        {error && <p style={{ color: '#c2410c' }}>{error}</p>}

        {!loading && !error && (
          <div
            style={{
              overflowX: 'auto',
              background: dark ? '#17232d' : '#fff',
              borderRadius: 8,
              border: `1px solid ${dark ? '#2c3b46' : '#dce5e8'}`,
            }}
          >
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 980 }}>
              <thead>
                <tr>
                  {[
                    'Symbol',
                    'Company',
                    'Score',
                    'GARP Match',
                    'Market cap',
                    'Price',
                    'Beta',
                    'Dividend',
                    'Sector',
                    'Exchange',
                  ].map((heading) => (
                    <th key={heading} style={thStyle}>
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 500).map(({ stock, passed, considered }) => (
                  <tr key={stock.symbol}>
                    <td style={tdStyle}>
                      <strong>{stock.symbol}</strong>
                    </td>
                    <td style={tdStyle}>{stock.companyName || '-'}</td>
                    <td
                      style={{
                        ...tdStyle,
                        color: scoreFor(stock) >= 75 ? '#0f766e' : '#a16207',
                        fontWeight: 800,
                      }}
                    >
                      {scoreFor(stock).toFixed(0)}
                    </td>
                    <td style={tdStyle}>
                      <span style={matchPillStyle(passed, considered)}>
                        {passed}/{considered || 7}
                      </span>
                    </td>
                    <td style={tdStyle}>{money(stock.marketCap)}</td>
                    <td style={tdStyle}>{stock.price == null ? '-' : `$${stock.price.toFixed(2)}`}</td>
                    <td style={tdStyle}>{stock.beta?.toFixed(2) ?? '-'}</td>
                    <td style={tdStyle}>{stock.lastAnnualDividend?.toFixed(2) ?? '-'}</td>
                    <td style={tdStyle}>{stock.sector || '-'}</td>
                    <td style={tdStyle}>{stock.exchangeShortName || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </main>
  );
};

const inputStyle = (dark: boolean): React.CSSProperties => ({
  padding: '9px 11px',
  borderRadius: 5,
  border: `1px solid ${dark ? '#40515d' : '#cbd8dc'}`,
  background: dark ? '#101820' : '#fff',
  color: dark ? '#f3f4f6' : '#17232b',
});

const controlGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
  gap: 14,
  alignItems: 'end',
};

const actionRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 10,
  flexWrap: 'wrap',
  marginTop: 16,
  paddingTop: 16,
  borderTop: '1px solid rgba(96, 113, 123, 0.18)',
};

const panelHeadingStyle = (dark: boolean): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: 12,
  marginBottom: 16,
  color: dark ? '#f3f4f6' : '#17232b',
  fontSize: 15,
  fontWeight: 800,
});

const panelHintStyle = (dark: boolean): React.CSSProperties => ({
  color: dark ? '#9ca3af' : '#71818a',
  fontSize: 12,
  fontWeight: 400,
});

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '13px 14px',
  fontSize: 12,
  color: '#60717b',
  borderBottom: '1px solid #dce5e8',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 14px',
  borderBottom: '1px solid #e7eef0',
  fontSize: 13,
  whiteSpace: 'nowrap',
};

const ctaButtonStyle: React.CSSProperties = {
  border: 'none',
  borderRadius: 6,
  background: '#0f766e',
  color: '#ffffff',
  fontWeight: 700,
  padding: '10px 14px',
  cursor: 'pointer',
};

const ghostButtonStyle: React.CSSProperties = {
  borderRadius: 6,
  border: '1px solid #8ea0aa',
  background: 'transparent',
  color: '#0f766e',
  fontWeight: 700,
  padding: '10px 14px',
  cursor: 'pointer',
};

const matchPillStyle = (passed: number, considered: number): React.CSSProperties => {
  const ratio = considered <= 0 ? 0 : passed / considered;
  const background = ratio >= 0.8 ? '#dcfce7' : ratio >= 0.55 ? '#fef3c7' : '#fee2e2';
  const color = ratio >= 0.8 ? '#166534' : ratio >= 0.55 ? '#92400e' : '#991b1b';
  return {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: 999,
    background,
    color,
    fontWeight: 700,
    fontSize: 12,
  };
};

export default ScreenerScreen;
